import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, Loader2 } from 'lucide-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import createApiClient from '@/api/axiosInstance';

const ResetPasswordScreen = ({ reset_token, setResetDone }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [pswrd, setPswrd] = useState('');
  const [cnfpswrd, setCnfPswrd] = useState('');

  const [pswrdValid, setPswrdValid] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const [message, setMessage] = useState(['', '']);
  const [incorrect, setIncorrect] = useState(['', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = () => {
    if (pswrd && pswrd.length < 8) {
      updateMessage(0, 'Password must be at least 8 characters.');
      updateIncorrect(0, true);
    } else {
      updateMessage(0, '');
      updateIncorrect(0, false);
    }
  };

  const validateConfirmPassword = () => {
    if (cnfpswrd !== pswrd) {
      updateMessage(1, 'Passwords do not match.');
      updateIncorrect(1, true);
    } else {
      updateMessage(1, '');
      updateIncorrect(1, false);
    }
  };

  const updateMessage = (index, msg) => {
    setMessage((prevMessages) => {
      let updatedMessages = [...prevMessages];
      updatedMessages[index] = msg;
      return updatedMessages;
    });
  };

  const updateIncorrect = (index, incorrect) => {
    setIncorrect((prev) => {
      let updated = [...prev];
      updated[index] = incorrect;
      return updated;
    });
  };

  useEffect(() => {
    let verify = pswrd.length >= 8;
    setPswrdValid(verify);
  }, [pswrd]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pswrd || !cnfpswrd) {
      if (!pswrd) {
        updateMessage(0, 'New Password is required.');
        updateIncorrect(0, true);
      }
      if (!cnfpswrd) {
        updateMessage(1, 'Confirm Password is required');
        updateIncorrect(1, true);
      }

      return;
    }

    updateIncorrect(0, false);
    updateIncorrect(1, false);
    validatePassword();
    validateConfirmPassword();

    if (message.every((msg) => msg === '')) {
      setIsLoading(true);

      try {
        // Step 1: Reset password
        const response = await apiClient.post(
          '/reset-password',
          JSON.stringify({
            reset_token,
            new_password: pswrd,
          }),
          {
            authRequired: false,
          },
        );

        const data = response.data;
        setResetDone(true);
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Network or Unexpected Error:', error_message);
        setError(error_message || 'Network error. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col space-y-3 items-center justify-center border-gray-400 rounded-lg px-12 w-5/6">
      <div className="flex flex-col items-start w-full">
        <span className="font-semibold mb-1 text-gray-500">New Password</span>
        <input
          type="password"
          value={pswrd}
          onFocus={() => setPasswordFocused(true)}
          onBlur={() => {
            setPasswordFocused(false);
            validatePassword();
          }}
          onChange={(e) => setPswrd(e.target.value)}
          className={`rounded-md bg-gray-100 border px-4 py-2 w-full ${
            incorrect[0]
              ? 'outline-red-400  border-red-400 hover:border-red-500'
              : 'outline-veryLightPurple  transition ease-in-out duration-300 border-gray-400/70 hover:border-lightPurple'
          }`}
          placeholder="Enter your password"
        />
        <div
          className={`flex text-[12px] mt-1 transition-all duration-300  ${
            passwordFocused ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {pswrdValid ? (
            <div className="flex flex-row items-center text-green-500">
              <CheckIcon className="h-3.5 w-3.5 mr-1" />
              <span>Password must be at least 8 characters long</span>
            </div>
          ) : (
            <div className="flex flex-row items-center text-[#B64444]">
              <XMarkIcon className="h-3.5 w-3.5 mr-1" />
              <span>Password must be at least 8 characters long</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-start w-full">
        <span className="font-semibold mb-1 text-gray-500">
          Confirm Password
        </span>
        <input
          type="password"
          value={cnfpswrd}
          onBlur={() => validateConfirmPassword()}
          onChange={(e) => setCnfPswrd(e.target.value)}
          className={`rounded-md bg-gray-100 border px-4 py-2 w-full ${
            incorrect[1]
              ? 'outline-red-400  border-red-400 hover:border-red-500'
              : 'outline-veryLightPurple  transition ease-in-out duration-300 border-gray-400/70 hover:border-lightPurple'
          }`}
          placeholder="Re-Enter password"
        />
      </div>
      {message.some((msg) => msg !== '') && (
        <div className="mt-3 py-3 px-3 rounded-lg bg-[#F8D7D7] text-[#B64444] text-[10px] w-full">
          {message.map((m, i) => (
            <p className="text-sm" key={i}>
              {m}
            </p>
          ))}
        </div>
      )}
      {error && (
        <div className="mt-3 py-3 px-3 rounded-lg bg-[#F8D7D7] text-[#B64444] text-[10px] w-full">
          <p className="text-sm">{error}</p>
        </div>
      )}
      <button
        onClick={(e) => handleSubmit(e)}
        disabled={isLoading}
        className={`w-full text-white font-roboto font-semibold rounded-lg mt-3 p-2 text-lg transition ease-in-out duration-300
          ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-darkPurple hover:bg-darkerPurple'
          } flex items-center justify-center`}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Reseting...
          </>
        ) : (
          'Reset Password'
        )}
      </button>
    </div>
  );
};

export default ResetPasswordScreen;
